import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Typography, Card as AntCard, Button, message } from "antd";
import { Column } from "@ant-design/plots";

import { DEFAULT_FOOTER, FooterOptions } from "../../types/dashboard";

import ContentGrid from "./components/ContentGrid";
import FooterSection from "./components/FooterSection";
import FilterChipsBar from "./components/FilterChipsBar";

import type {
  DashboardAcceptedApi,
  DashboardTabApi,
  DashboardCardWithData,
  DashboardColumnApi,
} from "../../types/types";
import EditCardModal from "./modals/EditCardModal";

import LocationMapCard from "./InsightCard/LocationMapCard";
import BscCard from "./InsightCard/BscCard";
import GeoHotspotsCard from "./InsightCard/GeoHotspotsCard";
import {
  AnomalyPreview,
  CriticalMetricPreview,
  HeatmapPreview,
  HistogramPreview,
  ProgressPreview,
  TreemapPreview,
  TrendAnalysisPreview,
  WordCloudPreview,
} from "./InsightCard/InsightPreviewCards";

const { Text } = Typography;

type Props = {
  tab: DashboardTabApi;
  dashboard: DashboardAcceptedApi;
  cards: DashboardCardWithData[];
  onOpenAdd: () => void;
  onDeleteCard: (cardId: string) => void;
  onApplyDrillDown: (filters: Record<string, any>) => void;
  onClearDrillDown: () => void;
  drillDownFilters: Record<string, any>;
  columns: DashboardColumnApi[];
  onUpdateCard: (cardId: string, payload: Record<string, any>) => Promise<void>;
  readOnly?: boolean;
};

const Block: React.FC<{ title: string; body?: React.ReactNode }> = ({
  title,
  body,
}) => (
  <AntCard
    style={{ width: "100%" }}
    bodyStyle={{ minHeight: 140 }}
    title={<Text strong>{title}</Text>}
    bordered
  >
    {body ?? <Text type="secondary">No content</Text>}
  </AntCard>
);

const toFooterOptions = (dashboard: DashboardAcceptedApi): FooterOptions => {
  const f = dashboard.footer_config || {};
  return {
    ...DEFAULT_FOOTER,
    textColor: f.font_color ?? DEFAULT_FOOTER.textColor,
    fontSize: Number(f.font_size ?? DEFAULT_FOOTER.fontSize),
    fontFamily: f.font_type ?? DEFAULT_FOOTER.fontFamily,
    paddingX: Number(
      String(f.padding_left ?? DEFAULT_FOOTER.paddingX).replace("px", ""),
    ),
    paddingY: DEFAULT_FOOTER.paddingY,
    paddingLeft: Number(
      String(
        f.padding_left ?? DEFAULT_FOOTER.paddingLeft ?? DEFAULT_FOOTER.paddingX,
      ).replace("px", ""),
    ),
    paddingRight: Number(
      String(
        f.padding_right ??
          DEFAULT_FOOTER.paddingRight ??
          DEFAULT_FOOTER.paddingX,
      ).replace("px", ""),
    ),
    lineHeight: f.line_height ?? DEFAULT_FOOTER.lineHeight,
    backgroundColor: f.background_color ?? DEFAULT_FOOTER.backgroundColor,
    borderColor: f.border_color ?? DEFAULT_FOOTER.borderColor,
    borderWidth: Number(f.border_stroke ?? DEFAULT_FOOTER.borderWidth),
    radius: Number(f.border_radius ?? DEFAULT_FOOTER.radius),
    imageUrl: f.image_url ?? "",
    imageAlign: String(f.image_alignment ?? "right").toLowerCase() as
      | "left"
      | "right",
  };
};

const DashboardTemplate: React.FC<Props> = ({
  tab,
  dashboard,
  cards,
  onOpenAdd,
  onDeleteCard,
  onApplyDrillDown,
  onClearDrillDown,
  drillDownFilters,
  columns,
  onUpdateCard,
  readOnly = false,
}) => {
  const [footerLeft, setFooterLeft] = useState("");
  const [footerRight, setFooterRight] = useState(
    `© ${new Date().getFullYear()} Your Company`,
  );
  const [editingFooterLeft, setEditingFooterLeft] = useState(false);
  const [editingFooterRight, setEditingFooterRight] = useState(false);
  const [footerOptions, setFooterOptions] =
    useState<FooterOptions>(DEFAULT_FOOTER);
  const [editCardOpen, setEditCardOpen] = useState(false);
  const [cardToEdit, setCardToEdit] = useState<DashboardCardWithData | null>(
    null,
  );
  const [editSaving, setEditSaving] = useState(false);

  useEffect(() => {
    setFooterLeft(
      dashboard.footer_config?.title ||
        "This report was produced by International Advisory, Products and Systems Ltd. (i-APS)",
    );
    setFooterOptions(toFooterOptions(dashboard));
  }, [dashboard, tab.id]);

  const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL ||
    "https://developmentapi.kapinsights.com";

  const getAbsoluteImageUrl = (path?: string | null) => {
    if (!path) return "";
    if (path.startsWith("http://") || path.startsWith("https://")) return path;
    return `${API_BASE_URL}${path}`;
  };

  const getCardRows = (data: any) => {
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data)) return data;
    return [];
  };

  const getNumericValue = (value: any, fallback = 0) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : fallback;
  };

  const isBadKPIName = (name?: string) => {
    const lower = String(name || "").toLowerCase();
    return (
      lower.includes("id") ||
      lower.includes("timestamp") ||
      lower.includes("time") ||
      lower.includes("date")
    );
  };

  const normalizeGeoHotspots = (
    rows: any[],
    card?: DashboardCardWithData,
  ) => {
    if (!rows.length) return [];

    const keys = Object.keys(rows[0]);
    const coordKeys = ["lat", "lng", "latitude", "longitude", "long", "lon"];
    const isCoord = (k: string) => coordKeys.includes(k.toLowerCase());

    const configuredDim = card?.dimensions?.[0]?.column;
    const configuredMetric = card?.metrics?.[0]?.column;

    const cityKey =
      (configuredDim && keys.includes(configuredDim) && configuredDim) ||
      keys.find((k) => k.toLowerCase().includes("city")) ||
      keys.find((k) => k.toLowerCase().includes("location")) ||
      keys.find((k) => !isCoord(k) && typeof rows[0][k] === "string");

    const stateKey = keys.find((k) => k.toLowerCase().includes("state"));

    // Metric detection must exclude lat/lng — otherwise a coordinate column
    // gets aggregated (and negative lng values wipe the card via value>0).
    const metricKey =
      (configuredMetric &&
        keys.includes(configuredMetric) &&
        configuredMetric) ||
      keys.find(
        (k) =>
          !isCoord(k) &&
          k !== cityKey &&
          k !== stateKey &&
          !k.toLowerCase().includes("city") &&
          !k.toLowerCase().includes("state") &&
          !k.toLowerCase().includes("location") &&
          typeof rows[0][k] !== "string",
      );

    if (!cityKey || !metricKey) return [];

    const grouped = rows.reduce(
      (
        acc: Record<
          string,
          { value: number; lat: number | null; lng: number | null }
        >,
        row: any,
      ) => {
        const city = String(row[cityKey] || "Unknown");
        const state = stateKey ? String(row[stateKey] || "") : "";
        const label = state ? `${city}, ${state}` : city;
        const value = Number(row[metricKey] ?? 0);
        if (!Number.isFinite(value)) return acc;

        const lat = Number(row.lat ?? row.Latitude ?? row.latitude);
        const lng = Number(row.lng ?? row.Longitude ?? row.longitude);

        const entry = acc[label] || { value: 0, lat: null, lng: null };
        entry.value += value;
        if (
          entry.lat === null &&
          Number.isFinite(lat) &&
          Number.isFinite(lng)
        ) {
          entry.lat = lat;
          entry.lng = lng;
        }
        acc[label] = entry;
        return acc;
      },
      {},
    );

    return Object.entries(grouped)
      .map(([label, { value, lat, lng }]) => ({ label, value, lat, lng }))
      .filter((item) => item.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, 50);
  };

  const normalizeLocationRows = (rows: any[]) => {
    return rows
      .filter((row: any) => {
        return (
          row.City ||
          row.city ||
          row.Location ||
          row.location ||
          row.State ||
          row.state ||
          row.lat ||
          row.lng
        );
      })
      // Rows already carry backend-resolved lat/lng; surface mappable rows first
      // so the 12-row cap doesn't get consumed by unresolvable locations.
      .sort((a: any, b: any) => {
        const aHas = Number.isFinite(a.lat) && Number.isFinite(a.lng) ? 0 : 1;
        const bHas = Number.isFinite(b.lat) && Number.isFinite(b.lng) ? 0 : 1;
        return aHas - bHas;
      })
      .slice(0, 12)
      .map((row: any) => ({
        city:
          row.City ||
          row.city ||
          row.Location ||
          row.location ||
          "Unknown location",
        state: row.State || row.state || "",
        sentiment: row.Sentiment || row.sentiment || "",
        score:
          row["CSAT Score"] ??
          row["Csat Score"] ??
          row.score ??
          row.value ??
          null,
        channel: row.Channel || row.channel || "",
        reason: row.Reason || row.reason || "",
        lat: row.lat ?? null,
        lng: row.lng ?? null,
      }));
  };

  const handleDataPointClick = useCallback(
    (column: string, value: any) => {
      let filterColumn = column;
      let filterType: "equals" | "contains" = "equals";

      if (column.startsWith("__contains__")) {
        filterColumn = column.replace("__contains__", "");
        filterType = "contains";
      }

      const newFilters = {
        ...drillDownFilters,
        [filterColumn]: { type: filterType, value },
      };

      onApplyDrillDown(newFilters);
    },
    [drillDownFilters, onApplyDrillDown],
  );

  const handleRemoveFilter = useCallback(
    (column: string) => {
      const next = { ...drillDownFilters };
      delete next[column];

      if (Object.keys(next).length === 0) {
        onClearDrillDown();
      } else {
        onApplyDrillDown(next);
      }
    },
    [drillDownFilters, onApplyDrillDown, onClearDrillDown],
  );

  const renderBackendCard = (card: DashboardCardWithData) => {
    if ((card as any).error) {
      return (
        <Block
          title={card.title}
          body={<Text type="danger">{(card as any).error}</Text>}
        />
      );
    }

    switch (card.card_type) {
      case "critical_metric": {
        const metricKey = card.metrics?.[0]?.column;
        if (isBadKPIName(metricKey)) return null;
        return <CriticalMetricPreview card={card} />;
      }

      case "progress": {
        const rawRows = getCardRows(card.data);
        const metricKey = card.metrics?.[0]?.column;
        if (!metricKey || !rawRows.length) return null;
        return <ProgressPreview card={card} />;
      }

      case "trend_analysis": {
        const raw = getCardRows(card.data);
        if (!raw.length) return null;
        return (
          <TrendAnalysisPreview
            card={card}
            onDataPointClick={handleDataPointClick}
          />
        );
      }

      case "anomaly_detection": {
        const raw = getCardRows(card.data);
        if (!raw.length) return null;
        return <AnomalyPreview card={card} />;
      }

      case "sentiment_overview": {
        const distribution = card.data?.distribution || {};
        const sentimentData = Object.entries(distribution).map(
          ([key, value]) => ({
            type: key,
            count: getNumericValue(value, 0),
          }),
        );
        if (!sentimentData.length) return null;
        return (
          <Block
            title={card.title}
            body={
              <Column
                data={sentimentData}
                xField="type"
                yField="count"
                height={160}
              />
            }
          />
        );
      }

      case "histogram": {
        return (
          <HistogramPreview
            card={card}
            onDataPointClick={handleDataPointClick}
          />
        );
      }

      case "heatmap": {
        const raw = getCardRows(card.data);
        const dimensionKey = card.dimensions?.[0]?.column;
        const metricKey = card.metrics?.[0]?.column;
        if (!dimensionKey || !metricKey || !raw.length) return null;
        return (
          <HeatmapPreview
            card={card}
            onDataPointClick={handleDataPointClick}
          />
        );
      }

      case "treemap": {
        const raw = getCardRows(card.data);
        const dimensionKey = card.dimensions?.[0]?.column;
        const metricKey = card.metrics?.[0]?.column;
        if (!dimensionKey || !metricKey || raw.length < 2) return null;
        return (
          <TreemapPreview
            card={card}
            onDataPointClick={handleDataPointClick}
          />
        );
      }

      case "word_cloud": {
        const words = Array.isArray(card.data?.words) ? card.data.words : [];
        if (!words.length) return null;
        return (
          <WordCloudPreview
            card={card}
            onDataPointClick={handleDataPointClick}
          />
        );
      }

      case "image": {
        const imageSrc = getAbsoluteImageUrl(card.image_path);
        return (
          <Block
            title={card.title}
            body={
              imageSrc ? (
                <img
                  src={imageSrc}
                  alt={card.title || "Image card"}
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                  style={{
                    width: "100%",
                    height: 220,
                    objectFit: "cover",
                    borderRadius: 8,
                    display: "block",
                  }}
                />
              ) : (
                <Text type="secondary">No image available</Text>
              )
            }
          />
        );
      }

      case "text_box":
        return (
          <Block
            title={card.title}
            body={
              card.text_content ? (
                <div
                  style={{
                    background: "#fafafa",
                    border: "1px solid #e5e7eb",
                    borderRadius: 12,
                    padding: 16,
                    fontSize: 14,
                    lineHeight: 1.6,
                    color: "#555",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {card.text_content}
                </div>
              ) : (
                <Text type="secondary">No text available</Text>
              )
            }
          />
        );

      case "location_map": {
        const raw = getCardRows(card.data);
        const normalized = normalizeLocationRows(raw);
        return (
          <Block
            title={card.title}
            body={<LocationMapCard data={normalized} />}
          />
        );
      }

      case "geographic_performance_hotspots": {
        const raw = getCardRows(card.data);
        const normalized = normalizeGeoHotspots(raw, card);
        return (
          <Block
            title={card.title}
            body={<GeoHotspotsCard data={normalized} />}
          />
        );
      }

      case "bsc":
        return (
          <Block
            title="Balanced Scorecard"
            body={<BscCard projectId={(card as any).project_id} />}
          />
        );

      default:
        return (
          <Block
            title={card.title}
            body={
              <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>
                {JSON.stringify(card.data, null, 2)}
              </pre>
            }
          />
        );
    }
  };

  const visibleCards = useMemo(() => {
    return cards
      .slice()
      .sort((a, b) => a.order_index - b.order_index)
      .filter((card) => {
        if (card.card_type === "critical_metric") {
          const metricKey = card.metrics?.[0]?.column;
          return !isBadKPIName(metricKey);
        }
        if (card.card_type === "progress") {
          const rawRows = getCardRows(card.data);
          const metricKey = card.metrics?.[0]?.column;
          if (!metricKey || !rawRows.length) return false;
          const values = rawRows.map((r: any) =>
            getNumericValue(r[metricKey], 0),
          );
          const avg =
            values.reduce((sum: number, v: number) => sum + v, 0) /
            values.length;
          const percent =
            avg >= 0 && avg <= 1 ? Math.round(avg * 100) : Math.round(avg);
          return percent > 0;
        }
        if (card.card_type === "sentiment_overview") {
          const distribution = card.data?.distribution || {};
          return Object.keys(distribution).length > 0;
        }
        if (card.card_type === "treemap") {
          const raw = getCardRows(card.data);
          const dimensionKey = card.dimensions?.[0]?.column;
          const metricKey = card.metrics?.[0]?.column;
          return !!dimensionKey && !!metricKey && raw.length >= 2;
        }
        if (card.card_type === "heatmap") {
          const raw = getCardRows(card.data);
          const dimensionKey = card.dimensions?.[0]?.column;
          const metricKey = card.metrics?.[0]?.column;
          return !!dimensionKey && !!metricKey && raw.length > 0;
        }
        return true;
      });
  }, [cards]);

  const mappedCards = useMemo(
    () =>
      visibleCards.map((card) => ({
        id: card.id,
        type: "backend-card",
        title: card.title,
        props: {},
        backendCard: card,
      })),
    [visibleCards],
  );

  const cardLibrary = {
    "backend-card": {
      label: "Backend Card",
      render: (card: any) => renderBackendCard(card.backendCard),
    },
  };

  const handleEditCard = (cardId: string) => {
    const card = cards.find((c) => c.id === cardId) || null;
    setCardToEdit(card);
    setEditCardOpen(true);
  };

  const handleSaveEditedCard = async (payload: Record<string, any>) => {
    if (!cardToEdit) return;
    try {
      setEditSaving(true);
      await onUpdateCard(cardToEdit.id, payload);
      setEditCardOpen(false);
      setCardToEdit(null);
    } finally {
      setEditSaving(false);
    }
  };

  return (
    <div id={`dashboard-printable-${tab.id}`}>
      <FilterChipsBar
        filters={drillDownFilters}
        onRemoveFilter={handleRemoveFilter}
        onClearAll={onClearDrillDown}
      />

      <ContentGrid
        cards={mappedCards as any}
        cardLibrary={cardLibrary as any}
        onRemove={onDeleteCard}
        onEdit={handleEditCard}
        onOpenAdd={onOpenAdd}
        readOnly={readOnly}
      />

      <FooterSection
        footerOptions={footerOptions}
        footerLeft={footerLeft}
        footerRight={footerRight}
        editingLeft={editingFooterLeft}
        editingRight={editingFooterRight}
        setFooterLeft={setFooterLeft}
        setFooterRight={setFooterRight}
        setEditingLeft={setEditingFooterLeft}
        setEditingRight={setEditingFooterRight}
        centered
      />

      <EditCardModal
        open={editCardOpen}
        card={cardToEdit}
        columns={columns}
        loading={editSaving}
        onClose={() => {
          setEditCardOpen(false);
          setCardToEdit(null);
        }}
        onSave={handleSaveEditedCard}
      />
    </div>
  );
};

export default DashboardTemplate;
